import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { ADMIN_ROLES } from '@avanta/shared/constants/admins';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageHeader } from 'components/PageHeader';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';
import { NextAppointments } from './components/NextAppointments';
import { RecentActivity } from './components/RecentActivity';

const SCHEDULE_SESSION_ROLES = new Set<string>([ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER]);
const HOME_NAME_MAX_LENGTH = 32;

const truncateName = (value: string): string =>
  value.length > HOME_NAME_MAX_LENGTH ? `${value.slice(0, HOME_NAME_MAX_LENGTH)}...` : value;

export const Home = (): ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const user = useUserStore(state => state.user);

  const userName = user?.name ?? '';
  const visibleName = truncateName(userName);
  const title = t('HomePage.heading', { name: visibleName });
  const description = t('HomePage.description');
  const canScheduleSession =
    user?.type === UserType.ENTERPRISE ||
    (user?.type === UserType.ADMIN_ENTERPRISE && SCHEDULE_SESSION_ROLES.has(user.role));

  return (
    <div className="flex flex-col gap-8 h-full">
      <PageHeader title={title} description={description}>
        {canScheduleSession && (
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={() =>
              navigate(ROUTES.APPOINTMENTS_RESERVE, { state: { flow: FLOW_MODES.search_patient } })
            }
          >
            {t('HomePage.scheduleSession.cta')}
          </Button>
        )}
      </PageHeader>
      {/* <SessionExtensionRequests /> */}
      <div className="am-4xl:flex-row am-4xl:gap-6 am-7xl:gap-12 flex flex-1 flex-col gap-12">
        <div className="am-4xl:flex-1 min-w-0">
          <NextAppointments />
        </div>
        <div className="am-4xl:mt-2 am-4xl:w-72.5 relative w-full shrink-0">
          <div className="flex flex-col gap-3 h-full">
            <Text variant="heading-xs-medium" className="text-denim-800 mt-0.5">
              {t('HomePage.recentActivity.heading')}
            </Text>
            <div className="flex flex-1">
              <RecentActivity />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
